import React from 'react'

/**
 * The `GTEesti` font renders the "TM" sequence (e.g. in the uppercased "appoin*tm*ent") as the
 * trademark glyph "™" on Safari via a font ligature. `font-variant-ligatures: none` and
 * `font-feature-settings` do NOT stop it in this case; the reliable fix is to place the two letters
 * in different DOM elements, because ligatures never form across an element boundary.
 *
 * `breakTmLigature` splits the string right before every "m"/"M" that follows a "t"/"T" and wraps
 * that segment in a <span>, so "APPOINTMENT" becomes "APPOINT" + <span>"MENT"</span>. The visible
 * text is unchanged (no extra characters, no line-break opportunity), so translation labels and
 * Page Designer copy stay untouched — nothing to remember on future updates.
 *
 * @param value - The already-resolved, visible string.
 * @param keyPrefix - Unique prefix for React keys when this runs inside a list/map.
 */
export const breakTmLigature = (value: string, keyPrefix = 'tm'): React.ReactNode => {
    if (!value || !/[tT][mM]/.test(value)) {
        return value
    }

    // Split before every "m"/"M" (no lookbehind, for broad regex-engine support).
    const segments = value.split(/(?=[mM])/g)

    return segments.map((seg, index) => {
        const prev = index > 0 ? segments[index - 1] : ''
        const boundaryIsTm = /[tT]$/.test(prev) && /^[mM]/.test(seg)
        return boundaryIsTm ? <span key={`${keyPrefix}-tm-${index}`}>{seg}</span> : <React.Fragment key={`${keyPrefix}-${index}`}>{seg}</React.Fragment>
    })
}
